/**
 * Meeting Retros Service
 *
 * Monthly security-meeting retrospectives: the analyst uploads the Teams
 * transcript, reviews the entity-scrubbed payload, generates an AI draft,
 * edits, and publishes the approved content to the customer portal.
 *
 * API endpoints (func-axn-analyst-api):
 * - GET    /api/tenants/:tenantId/retros
 * - POST   /api/tenants/:tenantId/retros            (multipart upload)
 * - GET    /api/tenants/:tenantId/retros/:retroId
 * - PATCH  /api/tenants/:tenantId/retros/:retroId
 * - POST   /api/tenants/:tenantId/retros/:retroId/generate
 * - POST   /api/tenants/:tenantId/retros/:retroId/publish
 * - DELETE /api/tenants/:tenantId/retros/:retroId
 */

import type { MeetingRetro, MeetingRetroStatus, RetroDraftContent } from '../data/types';
import type { BadgeVariant } from '../components/ui/Badge';
import {
  apiFetch,
  apiPost,
  apiPatch,
  apiDelete,
  getCurrentUserId,
  API_BASE_URL,
  type ApiResponse,
} from './api';

interface RetrosResponse {
  retros: MeetingRetro[];
}

interface RetroResponse {
  retro: MeetingRetro;
}

export async function getMeetingRetros(tenantId: string): Promise<ApiResponse<MeetingRetro[]>> {
  const result = await apiFetch<RetrosResponse>(`/tenants/${encodeURIComponent(tenantId)}/retros`);
  if (!result.success) {
    return { data: [], success: false, error: result.error };
  }
  return { data: result.data.retros, success: true };
}

export async function getMeetingRetro(
  tenantId: string,
  retroId: string
): Promise<ApiResponse<MeetingRetro>> {
  const result = await apiFetch<RetroResponse>(
    `/tenants/${encodeURIComponent(tenantId)}/retros/${encodeURIComponent(retroId)}`
  );
  if (!result.success) {
    return { data: null as unknown as MeetingRetro, success: false, error: result.error };
  }
  return { data: result.data.retro, success: true };
}

/**
 * Multipart upload — bypasses apiFetch's JSON content-type (the browser must
 * set the multipart boundary itself).
 */
export async function uploadMeetingRetro(
  tenantId: string,
  file: File,
  meetingDate: string,
  title: string
): Promise<ApiResponse<MeetingRetro>> {
  const form = new FormData();
  form.append('file', file);
  form.append('meetingDate', meetingDate);
  form.append('title', title);

  const headers: Record<string, string> = {};
  const userId = getCurrentUserId();
  if (userId) headers['x-user-id'] = userId;

  try {
    const response = await fetch(
      `${API_BASE_URL}/tenants/${encodeURIComponent(tenantId)}/retros`,
      { method: 'POST', headers, body: form }
    );
    const body = (await response.json().catch(() => ({}))) as RetroResponse & { error?: string };
    if (!response.ok) {
      return {
        data: null as unknown as MeetingRetro,
        success: false,
        error: body.error || `Upload failed (${response.status})`,
      };
    }
    return { data: body.retro, success: true };
  } catch {
    return { data: null as unknown as MeetingRetro, success: false, error: 'Upload failed' };
  }
}

/** Sends the scrubbed payload to the drafting model. Takes ~30-60s. */
export async function generateMeetingRetro(
  tenantId: string,
  retroId: string
): Promise<ApiResponse<MeetingRetro>> {
  const result = await apiPost<RetroResponse>(
    `/tenants/${encodeURIComponent(tenantId)}/retros/${encodeURIComponent(retroId)}/generate`,
    {}
  );
  if (!result.success) {
    return { data: null as unknown as MeetingRetro, success: false, error: result.error };
  }
  return { data: result.data.retro, success: true };
}

export async function updateMeetingRetro(
  tenantId: string,
  retroId: string,
  patch: {
    title?: string;
    meetingDate?: string;
    draft?: RetroDraftContent;
    addManualEntities?: string[];
  }
): Promise<ApiResponse<MeetingRetro>> {
  const result = await apiPatch<RetroResponse>(
    `/tenants/${encodeURIComponent(tenantId)}/retros/${encodeURIComponent(retroId)}`,
    patch
  );
  if (!result.success) {
    return { data: null as unknown as MeetingRetro, success: false, error: result.error };
  }
  return { data: result.data.retro, success: true };
}

export async function publishMeetingRetro(
  tenantId: string,
  retroId: string
): Promise<ApiResponse<MeetingRetro>> {
  const result = await apiPost<RetroResponse>(
    `/tenants/${encodeURIComponent(tenantId)}/retros/${encodeURIComponent(retroId)}/publish`,
    {}
  );
  if (!result.success) {
    return { data: null as unknown as MeetingRetro, success: false, error: result.error };
  }
  return { data: result.data.retro, success: true };
}

export async function deleteMeetingRetro(
  tenantId: string,
  retroId: string
): Promise<ApiResponse<void>> {
  const result = await apiDelete<Record<string, never>>(
    `/tenants/${encodeURIComponent(tenantId)}/retros/${encodeURIComponent(retroId)}`
  );
  if (!result.success) {
    return { data: undefined, success: false, error: result.error };
  }
  return { data: undefined, success: true };
}

// ============================================================================
// STATUS PRESENTATION
// ============================================================================

export const RETRO_STATUS_LABELS: Record<MeetingRetroStatus, string> = {
  scrubbed: 'Ready to draft',
  draft_ready: 'Draft in review',
  published: 'Published',
};

export function getRetroStatusVariant(status: MeetingRetroStatus): BadgeVariant {
  switch (status) {
    case 'published':
      return 'green';
    case 'draft_ready':
      return 'blue';
    default:
      return 'yellow';
  }
}
